import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

type NavChild = { to: string; label: string; icon: string; roles: string[] };
type NavItem = NavChild | { label: string; icon: string; roles: string[]; children: NavChild[] };

const links: NavItem[] = [
  { to: '/hoy',           label: 'Hoy',           icon: '☀️', roles: ['Agente'] },
  { to: '/mis-polizas',   label: 'Mis Pólizas',   icon: '📋', roles: ['Agente'] },
  {
    label: 'Ventas', icon: '💼', roles: ['SuperAdmin', 'Admin', 'Gerente', 'Agente'],
    children: [
      { to: '/prospectos', label: 'Prospectos', icon: '👥', roles: ['SuperAdmin', 'Admin', 'Gerente', 'Agente'] },
      { to: '/reportes',   label: 'Reportes',   icon: '📈', roles: ['SuperAdmin', 'Admin'] },
    ],
  },
  { to: '/reclutamiento', label: 'Reclutamiento', icon: '🎯', roles: ['SuperAdmin', 'Admin', 'Gerente'] },
  { to: '/agenda',        label: 'Agenda',        icon: '📅', roles: ['SuperAdmin', 'Admin', 'Gerente', 'Agente'] },
  { to: '/operaciones',   label: 'Operaciones',   icon: '⚙️', roles: ['SuperAdmin', 'Admin', 'Operaciones'] },
  { to: '/usuarios',      label: 'Usuarios',      icon: '🔑', roles: ['Operaciones'] },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { user, logout } = useAuth();
  const location = useLocation();

  const visibleLinks = links
    .filter(l => l.roles.includes(user?.rol || ''))
    .map(l => 'children' in l
      ? { ...l, children: l.children.filter(c => c.roles.includes(user?.rol || '')) }
      : l
    )
    .filter(l => !('children' in l) || l.children.length > 0);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      links
        .filter((l): l is Extract<NavItem, { children: NavChild[] }> => 'children' in l)
        .map(l => [l.label, l.children.some(c => location.pathname.startsWith(c.to))])
    )
  );
  const toggleGroup = (label: string) => setExpandedGroups(g => ({ ...g, [label]: !g[label] }));

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-40 w-64 flex flex-col transition-transform duration-300 ease-in-out
          lg:relative lg:translate-x-0 lg:z-auto lg:flex-shrink-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ background: 'linear-gradient(180deg, #1d3d6b 0%, #162e50 100%)' }}
      >
        {/* Logo */}
        <div className="px-5 pt-6 pb-5 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 shadow-md flex items-center justify-center">
            <img src="/logo-sidebar.png" alt="Life & Legacy" className="w-full h-full object-contain p-0.5" />
          </div>
          <div className="flex-1">
            <p className="text-white font-bold text-base leading-tight">Life & Legacy</p>
            <p className="text-white/50 text-xs">CRM</p>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-white/60 hover:text-white text-2xl leading-none w-7 h-7 flex items-center justify-center"
          >
            ×
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-thin">
          {visibleLinks.map(link => {
            if ('children' in link) {
              const groupActive = link.children.some(c => location.pathname.startsWith(c.to));
              const expanded = !!expandedGroups[link.label];
              return (
                <div key={link.label}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(link.label)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      groupActive && !expanded
                        ? 'bg-accent-400/90 text-white shadow-sm'
                        : 'text-white/70 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    <span className="text-base w-5 text-center">{link.icon}</span>
                    <span className="flex-1 text-left">{link.label}</span>
                    <span className={`text-xs transition-transform ${expanded ? 'rotate-90' : ''}`}>›</span>
                  </button>
                  {expanded && (
                    <div className="mt-0.5 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                      {link.children.map(child => {
                        const isActive = location.pathname.startsWith(child.to);
                        return (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            onClick={onClose}
                            className={() =>
                              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                isActive
                                  ? 'bg-accent-400/90 text-white shadow-sm'
                                  : 'text-white/70 hover:text-white hover:bg-white/10'
                              }`
                            }
                          >
                            <span className="text-base w-5 text-center">{child.icon}</span>
                            <span>{child.label}</span>
                            {isActive && (
                              <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white/80" />
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = link.to === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(link.to);
            return (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                onClick={onClose}
                className={() =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-accent-400/90 text-white shadow-sm'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`
                }
              >
                <span className="text-base w-5 text-center">{link.icon}</span>
                <span>{link.label}</span>
                {isActive && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-white/80" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-accent-400 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {user?.nombre?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.nombre}</p>
              <p className="text-white/50 text-xs">{user?.rol}</p>
            </div>
            <button
              onClick={logout}
              title="Cerrar sesión"
              className="text-white/40 hover:text-white/80 transition-colors text-lg leading-none"
            >
              ⎋
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
